/**
 * Справочник категорий расходов — таблица с inline-редактированием +
 * добавление + soft-delete (archive).
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchExpenseCategories, createExpenseCategory, updateExpenseCategory, deleteExpenseCategory,
  KIND_LABELS, KIND_COLORS,
  type ExpenseCategoryRow, type CategoryKind,
} from "@/api/finance";
import { ApiError } from "@/api/client";


const KINDS_ORDER: CategoryKind[] = ["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"];


export function FinanceCategoriesPage() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftKind, setDraftKind] = useState<CategoryKind>("other");
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState<CategoryKind>("other");
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["finance-categories", showArchived],
    queryFn: () => fetchExpenseCategories(showArchived),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<ExpenseCategoryRow> }) =>
      updateExpenseCategory(id, { name: patch.name, kind: patch.kind, archived: patch.archived }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-categories"] });
      setEditingId(null);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message),
  });
  const createMut = useMutation({
    mutationFn: (payload: { name: string; kind: CategoryKind }) =>
      createExpenseCategory(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-categories"] });
      setCreatingNew(false);
      setNewName("");
      setNewKind("other");
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteExpenseCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-categories"] });
      setError(null);
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        const detail = (e.body as { detail?: string })?.detail;
        setError(detail || `HTTP ${e.status}`);
      } else {
        setError((e as Error).message);
      }
    },
  });

  const grouped = useMemo(() => {
    const out: Record<string, ExpenseCategoryRow[]> = {};
    for (const c of (q.data || [])) {
      if (!out[c.kind]) out[c.kind] = [];
      out[c.kind].push(c);
    }
    return out;
  }, [q.data]);

  if (q.isLoading) return <Spinner size="lg" label="Загрузка категорий…" />;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div className="alert alert-amber">
          <div className="alert-icon"><Icon name="warn" size={16}/></div>
          <div className="alert-body">
            <div className="alert-title">Доступ ограничен</div>
            <div>Финансовый раздел доступен только администратору и руководителю отдела.</div>
          </div>
        </div>
      );
    }
    const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }

  function startEdit(c: ExpenseCategoryRow) {
    setEditingId(c.id);
    setDraftName(c.name);
    setDraftKind(c.kind);
  }
  function saveEdit(c: ExpenseCategoryRow) {
    if (!draftName.trim()) {
      setError("Название не может быть пустым");
      return;
    }
    updateMut.mutate({ id: c.id, patch: { name: draftName.trim(), kind: draftKind } });
  }
  function toggleArchive(c: ExpenseCategoryRow) {
    if (c.archived) {
      updateMut.mutate({ id: c.id, patch: { archived: false } });
    } else if (c.usage_count === 0) {
      // Не используется → можно сразу удалить (на бэке = archived=true)
      deleteMut.mutate(c.id);
    } else {
      // Используется → архивируем (бэк-DELETE на этом случае вернёт 409)
      updateMut.mutate({ id: c.id, patch: { archived: true } });
    }
  }
  function submitNew() {
    if (!newName.trim()) {
      setError("Введите название категории");
      return;
    }
    createMut.mutate({ name: newName.trim(), kind: newKind });
  }

  return (
    <FinanceLayout>
            <div className="filter-bar" style={{ justifyContent: "space-between", marginBottom: "var(--gap)" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-3)" }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Показать архивные
          </label>
        </div>
        <button className="btn btn-primary" onClick={() => setCreatingNew(true)}>
          <Icon name="plus" size={12}/> Новая категория
        </button>
      </div>

      {error && (
        <div className="alert alert-red" style={{ marginBottom: "var(--gap)" }}>
          <div className="alert-body">{error}</div>
          <button className="btn btn-sm" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {creatingNew && (
        <div className="card" style={{ marginBottom: "var(--gap)", padding: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <label style={{ flex: 1, minWidth: 220, display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
              Название
              <input
                type="text" className="input" value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Например, Реклама — баннеры"
                autoFocus
              />
            </label>
            <label style={{ width: 200, display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
              Тип
              <select className="input" value={newKind} onChange={e => setNewKind(e.target.value as CategoryKind)}>
                {KINDS_ORDER.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
              </select>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={() => { setCreatingNew(false); setNewName(""); }}>Отмена</button>
              <button className="btn btn-primary" onClick={submitNew} disabled={createMut.isPending}>
                {createMut.isPending ? "Создание…" : "Добавить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {KINDS_ORDER.map(kind => {
        const list = grouped[kind] || [];
        if (list.length === 0) return null;
        return (
          <div key={kind} className="card" style={{ marginBottom: "var(--gap)" }}>
            <div className="card-h">
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: KIND_COLORS[kind] }}/>
                <h3 className="card-title">{KIND_LABELS[kind]}</h3>
                <span style={{ fontSize: 12, color: "var(--mute)" }}>· {list.length}</span>
              </div>
            </div>
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th style={{ width: 160 }}>Тип</th>
                    <th className="num" style={{ width: 110 }}>Операций</th>
                    <th style={{ width: 110 }}>Статус</th>
                    <th style={{ width: 130 }}/>
                  </tr>
                </thead>
                <tbody>
                  {list.map(c => {
                    const isEditing = editingId === c.id;
                    return (
                      <tr key={c.id} style={{ opacity: c.archived ? 0.55 : 1 }}>
                        <td>
                          {isEditing ? (
                            <input
                              type="text" className="input" value={draftName}
                              onChange={e => setDraftName(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") saveEdit(c); if (e.key === "Escape") setEditingId(null); }}
                              autoFocus
                            />
                          ) : (
                            <span className="strong">{c.name}</span>
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <select className="input" value={draftKind} onChange={e => setDraftKind(e.target.value as CategoryKind)}>
                              {KINDS_ORDER.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
                            </select>
                          ) : (
                            <span>{KIND_LABELS[c.kind]}</span>
                          )}
                        </td>
                        <td className="num tabular">{c.usage_count}</td>
                        <td>
                          {c.archived
                            ? <span className="badge" style={{ color: "var(--mute)" }}>архив</span>
                            : <span className="badge badge-green">активна</span>}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            {isEditing ? (
                              <>
                                <button className="btn btn-sm btn-primary" onClick={() => saveEdit(c)}>
                                  <Icon name="check" size={12}/>
                                </button>
                                <button className="btn btn-sm" onClick={() => setEditingId(null)}>
                                  <Icon name="x" size={12}/>
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="btn btn-sm" onClick={() => startEdit(c)} title="Редактировать">
                                  <Icon name="edit" size={12}/>
                                </button>
                                <button
                                  className="btn btn-sm" title={c.archived ? "Восстановить" : (c.usage_count > 0 ? "Архивировать" : "Удалить")}
                                  onClick={() => toggleArchive(c)}
                                  style={{ color: c.archived ? "var(--green)" : "var(--brick)" }}
                                >
                                  <Icon name={c.archived ? "refresh" : "trash"} size={12}/>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {(q.data?.length || 0) === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "32px 20px" }}>
          <div className="muted">Категорий не найдено</div>
        </div>
      )}
    </FinanceLayout>
  );
}
